import type { VariantProps } from 'class-variance-authority'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import * as React from 'react'

import { Children, cloneElement } from 'react'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*=\'size-\'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        'default': 'h-9 px-4 py-2 has-[>svg]:px-3',
        'sm': 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        'lg': 'h-10 rounded-md px-6 has-[>svg]:px-4',
        'icon': 'size-9',
        'xs': 'h-8 px-3 text-sm [&>svg]:size-3.5',
        // sm: "h-9 px-3 text-sm [&>svg]:size-4",
        'md': 'h-10 px-3.5 text-sm [&>svg]:size-5',
        // lg: "h-11 px-4 text-base [&>svg]:size-5",
        'xl': 'h-12 rounded-lg px-4.5 text-base [&>svg]:size-5',
        '2xl': 'h-14 rounded-lg px-5.5 text-lg [&>svg]:size-7',
        'icon-xxxs': 'size-6 [&>svg]:size-4.5',
        'icon-xxs': 'size-7 [&>svg]:size-4.5',
        'icon-xs': 'size-8 [&>svg]:size-4.5',
        'icon-sm': 'size-9 [&>svg]:size-5',
        'icon-md': 'size-10 [&>svg]:size-5',
        'icon-lg': 'size-11 [&>svg]:size-5',
        'icon-xl': 'size-12 [&>svg]:size-6',
        'icon-2xl': 'size-14 [&>svg]:size-7',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'>
  & VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

const buttonGroupVariants = cva('inline-flex', {
  variants: {
    variant: {
      default: '',
      destructive: '',
      outline: '',
      ghost: '',
    },
    size: {
      'xs': '',
      'sm': '',
      'md': '',
      'lg': '',
      'xl': '',
      '2xl': '',
    },
  },
  defaultVariants: {
    variant: 'outline',
    size: 'sm',
  },
})

interface ButtonGroupProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof buttonGroupVariants> {}
type TButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof buttonVariants> & { asChild?: boolean, isLoading?: boolean }
interface ButtonGroupProps {
  className?: string
  orientation?: 'horizontal' | 'vertical'
  children: React.ReactElement<TButtonProps>[]
}

function ButtonGroup({
  className,
  orientation = 'horizontal',
  children,
}: ButtonGroupProps) {
  const totalButtons = Children.count(children)
  const isHorizontal = orientation === 'horizontal'
  const isVertical = orientation === 'vertical'

  return (
    <div
      className={cn(
        'flex',
        {
          'flex-col': isVertical,
          'w-fit': isVertical,
        },
        className,
      )}
    >
      {Children.map(children, (child, index) => {
        const isFirst = index === 0
        const isLast = index === totalButtons - 1

        return cloneElement(child, {
          className: cn(
            {
              'rounded-l-none': isHorizontal && !isFirst,
              'rounded-r-none': isHorizontal && !isLast,
              'border-l-0': isHorizontal && !isFirst,

              'rounded-t-none': isVertical && !isFirst,
              'rounded-b-none': isVertical && !isLast,
              'border-t-0': isVertical && !isFirst,
            },
            child.props.className,
          ),
        })
      })}
    </div>
  )
}

ButtonGroup.displayName = 'ButtonGroup'

export { Button, ButtonGroup, buttonVariants }
